import { forwardRef, isValidElement, JSX, type ReactEventHandler } from 'react';

import {
  useIntent,
  UseIntentWithResponseReturn,
} from '../../../../core/hooks/intent.js';
import {
  ErrorRes,
  IntentWithoutResponseProps,
  SuccessRes,
} from '../../../../core/slots/Intent/Intent.js';
import { AppIcon } from '../../../../layouts/app-header/app-icon/AppIcon.js';
import { Item } from '../../../../navigation/menu/Item.js';
import { ItemIcon } from '../../../../navigation/menu/ItemIcon.js';

const isSrc = (icon: string | JSX.Element): icon is string => {
  return typeof icon === 'string';
};

const isIcon = (icon: string | JSX.Element): icon is JSX.Element => {
  return isValidElement(icon);
};

export const getHandleSelect =
  (
    onSelect: ReactEventHandler | undefined,
    intent: UseIntentWithResponseReturn,
    onResponse: (response: SuccessRes | ErrorRes) => void,
  ): ReactEventHandler =>
  async (e) => {
    onSelect?.(e);

    try {
      const response = await intent.send();
      onResponse(response);
    } catch {
      onResponse({
        status: 'error',
        error: new Error('Error sending intent with response'),
      });
    }
  };

export const IntentIcon = ({
  icon,
}: {
  icon?: string | JSX.Element | null;
}) => {
  if (!icon) {
    return null;
  }
  if (isSrc(icon)) {
    return <AppIcon src={icon} alt="" />;
  }
  if (isIcon(icon)) {
    const Icon = () => icon;
    return <Icon />;
  }
};

export const IntentWithoutResponse = /* @__PURE__ */ forwardRef<
  HTMLDivElement,
  IntentWithoutResponseProps
>((props, ref) => {
  const { onSelect, payload, options, children, ...rest } = props;

  const intent = useIntent({
    payload,
    options,
  });

  return (
    <Item
      {...rest}
      ref={ref}
      onSelect={(e) => {
        intent.send();
        onSelect?.(e);
      }}
    >
      {props.icon ?? intent.iconURL ? (
        <ItemIcon>
          <IntentIcon icon={props.icon ?? intent.iconURL} />
        </ItemIcon>
      ) : null}
      {children}
    </Item>
  );
});
